import { useLiveQuery } from '@electric-sql/pglite-react';
import { newId, type Sample, sampleTypeOptions } from '@quickreport/domain';
import { Button, Field, Input, Select } from '@quickreport/ui';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useWorkspace } from '../../lib/workspace';

const OTHER = '__other__';

function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Blood, Urine and Semen, then any type this lab has typed in before. */
export function useSampleTypeOptions(): string[] {
  const { orgId } = useWorkspace();
  const rows = useLiveQuery<{ name: string }>(
    'select distinct name from sample_types where org_id = $1 and deleted_at is null order by name',
    [orgId],
  );
  return sampleTypeOptions((rows?.rows ?? []).map((r) => r.name));
}

/**
 * The samples taken for a visit: what each one is, when it was collected and an optional
 * barcode or equipment number. A visit can have several, such as two bloods and a urine.
 */
export function SamplesEditor({
  samples,
  onChange,
  disabled,
  hint,
}: {
  samples: Sample[];
  onChange: (next: Sample[]) => void;
  disabled?: boolean;
  hint?: string;
}) {
  const options = useSampleTypeOptions();
  // Rows where the person chose "Other…" and is typing a new type.
  const [typing, setTyping] = useState<Set<string>>(new Set());
  const set = (id: string, patch: Partial<Sample>) =>
    onChange(samples.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const known = (t: string) => options.some((o) => o.toLowerCase() === t.trim().toLowerCase());
  const setTypingFor = (id: string, on: boolean) =>
    setTyping((p) => {
      const n = new Set(p);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  return (
    <section className="grid gap-2" aria-label="Samples">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-semibold">Samples</h3>
        {hint ? <span className="text-[12px] text-slate">{hint}</span> : null}
      </div>
      {samples.length === 0 ? (
        <p className="text-[12px] text-slate">No sample added.</p>
      ) : (
        <ul className="grid gap-2">
          {samples.map((s, i) => {
            const custom = typing.has(s.id) || (s.type !== '' && !known(s.type));
            return (
              <li
                key={s.id}
                className="grid grid-cols-[1fr_1fr] items-start gap-2 rounded-lg border border-line bg-canvas/60 p-2 md:grid-cols-[10rem_14.5rem_1fr_auto]"
              >
                {/* biome-ignore lint/a11y/noLabelWithoutControl: the control is inside, in one of two forms */}
                <label className="grid gap-1">
                  <span className="text-[12px] font-medium text-slate">Sample {i + 1}</span>
                  {custom ? (
                    <span className="flex gap-1">
                      <Input
                        aria-label={`Sample ${i + 1} type`}
                        value={s.type}
                        autoFocus={typing.has(s.id)}
                        placeholder="e.g. Saliva"
                        disabled={disabled}
                        onChange={(e) => set(s.id, { type: e.target.value })}
                      />
                      <button
                        type="button"
                        disabled={disabled}
                        title="Choose from the list"
                        aria-label="Choose from the list"
                        className="rounded px-1 text-slate hover:text-ink"
                        onClick={() => {
                          setTypingFor(s.id, false);
                          set(s.id, { type: options[0] ?? 'Blood' });
                        }}
                      >
                        <X size={13} />
                      </button>
                    </span>
                  ) : (
                    <Select
                      aria-label={`Sample ${i + 1} type`}
                      value={s.type}
                      disabled={disabled}
                      onChange={(e) => {
                        if (e.target.value === OTHER) {
                          setTypingFor(s.id, true);
                          set(s.id, { type: '' });
                        } else set(s.id, { type: e.target.value });
                      }}
                    >
                      {options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                      <option value={OTHER}>Other…</option>
                    </Select>
                  )}
                </label>
                <Field label="Collected">
                  <Input
                    type="datetime-local"
                    aria-label={`Sample ${i + 1} collected`}
                    value={toLocalInput(s.collectedAt)}
                    disabled={disabled}
                    onChange={(e) =>
                      set(s.id, {
                        collectedAt: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : undefined,
                      })
                    }
                  />
                </Field>
                <Field
                  label="Identifier (optional)"
                  hint="Barcode or tube number"
                  className="col-span-2 md:col-span-1"
                >
                  <Input
                    aria-label={`Sample ${i + 1} identifier`}
                    value={s.identifier ?? ''}
                    disabled={disabled}
                    onChange={(e) => set(s.id, { identifier: e.target.value || undefined })}
                  />
                </Field>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Remove sample ${i + 1}`}
                  title="Remove this sample"
                  className="mt-5 rounded p-1 text-slate hover:text-flag disabled:opacity-40"
                  onClick={() => onChange(samples.filter((x) => x.id !== s.id))}
                >
                  <X size={15} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {disabled ? null : (
        <div>
          <Button
            size="sm"
            onClick={() =>
              onChange([
                ...samples,
                {
                  id: newId(),
                  type: samples.at(-1)?.type || options[0] || 'Blood',
                  collectedAt: new Date().toISOString(),
                },
              ])
            }
          >
            <Plus size={13} /> Add sample
          </Button>
        </div>
      )}
    </section>
  );
}
